// @flow
import * as React from 'react';
import fuzzysearch from 'fuzzysearch';
import deepEqual from 'react-fast-compare';
import styled from 'styled-components';
import { isEmpty, sortBy, sort, isDefined } from 'utils/functional';

import SearchIcon from 'search.svg';
import Input from 'components/Input';
import Box from 'components/Box';
import SearchFilterSection from './SearchFilterSection';
import SearchFacetMultiOption from './SearchFacetMultiOption';
import { colors, textStyles } from '../theme';

const FACET_OPTIONS_FIRST_SHOWN_COUNT = 5;

const allOptionsSelected = ({ filter, facet }) =>
  (filter &&
    Array.isArray(filter) &&
    deepEqual(sort(filter), sort(facet.options))) ||
  filter === 'ALL';

const StyledSearchIcon = styled(SearchIcon)`
  position: absolute;
  left: 10px;
  top: 23px;
  color: ${colors.greyishBrown};
  opacity: 0.5;
`;

const InputContainer = styled.div`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  width: 100%;
  padding-bottom: 8px;
  padding-top: 8px;
  border-bottom: 1px solid ${colors.aluminum};
`;

const StyledInput = styled(Input)`
  border: 0;
  background: transparent;
  ${textStyles.bodySmall}
`;

const StyledSearchInput = ({ ...inputProps }: { 'data-qa-id': string }) => (
  <InputContainer>
    <StyledInput
      {...inputProps}
      height="100%"
      width="100%"
      p="12px 0 12px 36px"
      placeholder="Search"
    />
    <StyledSearchIcon width="16" height="16" />
  </InputContainer>
);

type Props = {
  facet: Object,
  filters: Object,
  setSearchFilter: Function,
  selectSearchFilterOption: Function,
  deselectSearchFilterOption: Function,
  selectAllSearchFilterOptions: Function,
  hasOptionFilter: boolean,
  noToggle?: boolean,
  expanded?: boolean,
  toggleExpanded?: Function
};

type State = {
  optionFilterTerm: string
};

export default class extends React.Component<Props, State> {
  state = {
    optionFilterTerm: ''
  };

  updateOptionFilterTerm = (ev: InputEvent) => {
    this.setState({ optionFilterTerm: ev.target.value });
  };

  renderFacetOption(option: any) {
    const {
      facet,
      filters,
      setSearchFilter,
      selectSearchFilterOption,
      deselectSearchFilterOption,
      selectAllSearchFilterOptions
    } = this.props;
    const filter = filters[facet.id];

    let isSelected;
    let toggle;

    if (facet.type === 'array') {
      isSelected =
        filter &&
        filter.includes(option) &&
        !allOptionsSelected({ filter, facet });
      toggle = () => {
        // If it is being deselected and is the only selected option, select all options
        if (isSelected && deepEqual([option], filter)) {
          selectAllSearchFilterOptions(facet.id);
          return;
        }

        if (isSelected) {
          deselectSearchFilterOption(facet.id, option);
          return;
        }

        if (allOptionsSelected({ filter, facet })) {
          setSearchFilter(facet.id, [option]);
          return;
        }

        selectSearchFilterOption(facet.id, option);
      };
    } else {
      isSelected = filter === option;
      toggle = () => setSearchFilter(facet.id, option);
    }

    return (
      <SearchFacetMultiOption
        key={option}
        value={option}
        title={facet.optionTitles[option]}
        isSelected={isSelected}
        toggle={() => {
          toggle();
          if (facet.facetToResetOnChange) {
            selectAllSearchFilterOptions(facet.facetToResetOnChange);
          }
        }}
      />
    );
  }

  render() {
    const {
      facet,
      filters,
      setSearchFilter,
      selectAllSearchFilterOptions,
      hasOptionFilter,
      noToggle
    } = this.props;

    if (facet.hideInSideFilter) {
      return null;
    }

    const filter = filters[facet.id];

    let facetOptions = facet.options;

    if (facet.optionCullFn) {
      facetOptions = facet.optionCullFn(filters);
    }

    const term = this.state.optionFilterTerm.toLowerCase();
    const filteredOptions = isEmpty(term)
      ? facetOptions
      : facetOptions.filter(o =>
          fuzzysearch(term, facet.optionTitles[o].toLowerCase())
        );

    const optionsOrderedAlphabetically = options =>
      sortBy(options, o => facet.optionTitles[o]);

    const optionsOrdered = ({
      options
    }: {
      options: Array<string>
    }): Array<string> => {
      if (isDefined(facet.sort) && !facet.sort) {
        // facet config says to not sort
        return options;
      }
      return optionsOrderedAlphabetically(options);
    };

    return (
      <SearchFilterSection
        title={facet.title}
        header={
          hasOptionFilter && (
            <StyledSearchInput
              data-qa-id="side-filter-facet-option-filter-input"
              onChange={this.updateOptionFilterTerm}
            />
          )
        }
        noToggle={noToggle}
        expanded={this.props.expanded}
        toggleExpanded={this.props.toggleExpanded}
        data-qa-id={`side-filter-facet-${facet.name}`}
        data-qa-filter-value={
          Array.isArray(filter) ? sort(filter).join() : filter
        }
        count={
          Array.isArray(filter) && filter.length < facet.options.length
            ? filter.length
            : undefined
        }
        hasOptionFilter={hasOptionFilter}
        tooltip={
          facet.name === 'locale'
            ? getText(
                'Use this filter to display listings that are written in one or more specific languages'
              )
            : undefined
        }
      >
        <Box pt={16}>
          {facet.type === 'array' ? (
            <SearchFacetMultiOption
              key="ALL"
              value="ALL"
              title={getText('All')}
              isSelected={allOptionsSelected({ filter, facet })}
              toggle={() => {
                if (!allOptionsSelected({ filter, facet })) {
                  selectAllSearchFilterOptions(facet.id);
                  if (facet.facetToResetOnChange) {
                    selectAllSearchFilterOptions(facet.facetToResetOnChange);
                  }
                }
              }}
            />
          ) : (
            (() => {
              const isSelected = filter === 'ALL';
              return (
                <SearchFacetMultiOption
                  key="ALL"
                  value="ALL"
                  title={getText('All')}
                  isSelected={isSelected}
                  toggle={() => {
                    if (!isSelected) setSearchFilter(facet.id, 'ALL');
                  }}
                />
              );
            })()
          )}

          {filteredOptions.length <= FACET_OPTIONS_FIRST_SHOWN_COUNT
            ? filteredOptions.map(option => this.renderFacetOption(option))
            : optionsOrdered({
                options: filteredOptions
              }).map(option => this.renderFacetOption(option))}
        </Box>
      </SearchFilterSection>
    );
  }
}
